/* eslint-disable max-lines */
import { Layer } from '@trimble/web3d-providers';
import {
  Component,
  createEffect,
  createMemo,
  createSignal,
  Show,
  onCleanup,
  For,
  on,
  onMount,
} from 'solid-js';
import { createStore, reconcile } from 'solid-js/store';
import { UnreachableCaseError } from 'ts-essentials';
import { IconContextMenuSelectEvent } from '@trimble/web3d-web-components';
import { LayerEditPopup } from '../layer-edit-popup/LayerEditPopup';
import { LayerRenamePopup } from '../layer-rename-popup/LayerRenamePopup';
import { LayerViewPopup } from '../layer-view-popup/LayerViewPopup';
import { BtnClasses, IconButton } from '@tcweb/connect-ui';
import { t } from '@trimble/i18n';

import { layerListPanelTracker } from '../analytics/layer-list-panel-tracker';

import {
  defineDropdownSelect,
  PanelHeaderSearchInput,
  PanelLayoutWithHeaderToggle,
} from '@trimble/ui-components/solidjs';
import { LayerListPanelAnalyticsEvent } from '../analytics/layer-list-panel-analytics-events';
import { LayerCreatePopup } from '../layer-create-popup/LayerCreatePopup';
import { LayerItem } from './LayerItem';
import {
  LayerScopeDropdownOptions,
  type LayerCategoryKey,
  type LayerItemObject,
  type LayerListPanelProps,
} from './LayerListPanel.types';

export type {
  LayerItemObject,
  LayerListPanelProps,
  LayerCategoryKey,
  SurfaceItem,
} from './LayerListPanel.types';
export { LayerScopeDropdownOptions } from './LayerListPanel.types';

enum ContextMenuItem {
  FitToView = 'fitToView',
  Edit = 'edit',
  Rename = 'rename',
  View = 'view',
  IsolateLayer = 'isolate_layer',
  UnisolateLayer = 'unisolate_layer',
  SelectItem = 'select_item',
  ExportToProjectData = 'export_to_project_data',
  Download = 'download',
  Delete = 'delete',
  SelectAll = 'select_all',
}

interface LayerCategory {
  readonly categoryKey: LayerCategoryKey;
  readonly category: string;
  readonly layers: readonly LayerItemObject[];
  readonly includeWhenEmpty: boolean;
}

export const LayerListPanel: Component<LayerListPanelProps> = (props) => {
  const [items, setItems] = createStore<LayerItemObject[]>([]);
  const [selectedGuids, setSelectedGuids] = createSignal<Set<string>>(
    new Set<string>()
  );
  const [layersScopeDropdown, setLayersScopeDropdown] =
    createSignal<LayerScopeDropdownOptions>(LayerScopeDropdownOptions.All);

  const [filterString, setFilterString] = createSignal('');
  const [editingLayer, setEditingLayer] = createSignal<Layer | undefined>(
    undefined
  );
  const [editPopupAnchor, setEditPopupAnchor] = createSignal<
    HTMLElement | undefined
  >(undefined);
  const [renamePopup, setRenamePopup] = createStore<{
    item: LayerItemObject | undefined;
    anchor: HTMLElement | undefined;
  }>({ item: undefined, anchor: undefined });

  const openRename = (item: LayerItemObject, anchor: HTMLElement): void => {
    setRenamePopup({ item, anchor });
  };

  const closeRename = (): void => {
    const anchor = renamePopup.anchor;
    if (anchor?.parentNode) {
      anchor.parentNode.removeChild(anchor);
    }
    setRenamePopup({ item: undefined, anchor: undefined });
  };

  const [viewingLayer, setViewingLayer] = createSignal<Layer | undefined>(
    undefined
  );
  const [viewPopupAnchor, setViewPopupAnchor] = createSignal<
    HTMLElement | undefined
  >(undefined);

  const layerMenuTriggers = new Map<string, HTMLElement>();

  const [isCreatePopupVisible, setIsCreatePopupVisible] =
    createSignal<boolean>(false);

  const [collapsedCategoryKeys, setCollapsedCategoryKeys] = createSignal<
    Set<LayerCategoryKey>
  >(
    // eslint-disable-next-line solid/reactivity -- initial collapse state only
    new Set<LayerCategoryKey>(
      props.initiallyCollapsedCategoryKeys ?? ['feature']
    )
  );

  createEffect(() => {
    setItems(reconcile(props.items, { key: 'guid' }));
  });

  /**
   * PDF pages are reference sheets with their own session visibility, so they
   * must not keep the header eye off or drive Show all.
   */
  const contributesToAllLayersVisible = (item: LayerItemObject): boolean =>
    item.category !== 'pdfPage' && !(item.visibilityToggleDisabled ?? false);

  const allLayersVisible = createMemo(() => {
    const relevant = items.filter(contributesToAllLayersVisible);
    return items.length > 0 && relevant.every((item) => item.visible);
  });

  /**
   * Feature layers and PDF pages are not part of the isolation workflow, so
   * they neither survive the Isolated scope nor activate it.
   */
  const isIsolatable = (item: LayerItemObject): boolean =>
    item.category !== 'feature' && item.category !== 'pdfPage';

  const scopedLayers = createMemo(() => {
    if (layersScopeDropdown() === LayerScopeDropdownOptions.All) {
      return items;
    } else {
      return items.filter((item) => isIsolatable(item) && item.isIsolated);
    }
  });

  const isolatedLayersDropdownOptions = [
    {
      key: LayerScopeDropdownOptions.All,
      label: t('shared.data-prep.all-layers'),
    },
    {
      key: LayerScopeDropdownOptions.Isolated,
      label: t('shared.data-prep.isolated-layers'),
    },
  ];

  const filteredItems = createMemo(() => {
    return scopedLayers()
      .filter((item) =>
        item.name.toLowerCase().includes(filterString().toLowerCase())
      )
      .sort(
        (a, b) =>
          a.name.localeCompare(b.name) ||
          a.id.toString().localeCompare(b.id.toString())
      );
  });

  const getCategoryKey = (item: LayerItemObject): LayerCategoryKey =>
    item.category ?? (item.isSurface ? 'surface' : 'cad');

  const layersByCategory = createMemo((): readonly LayerCategory[] => {
    const filtered = filteredItems();
    const cadLayers = filtered.filter((item) => getCategoryKey(item) === 'cad');
    const surfaceLayers = filtered.filter(
      (item) => getCategoryKey(item) === 'surface'
    );
    const featureLayers = filtered.filter(
      (item) => getCategoryKey(item) === 'feature'
    );
    const pdfPageLayers = filtered.filter(
      (item) => getCategoryKey(item) === 'pdfPage'
    );
    const hasFeatureLayers = featureLayers.length > 0;

    return [
      {
        categoryKey: 'cad',
        category: t('shared.data-prep.category-cad'),
        layers: cadLayers,
        includeWhenEmpty: !hasFeatureLayers,
      },
      {
        categoryKey: 'surface',
        category: t('shared.data-prep.category-surface'),
        layers: surfaceLayers,
        includeWhenEmpty: !hasFeatureLayers,
      },
      {
        categoryKey: 'pdfPage',
        category: t('shared.data-prep.category-pdf-page'),
        layers: pdfPageLayers,
        includeWhenEmpty: false,
      },
      {
        categoryKey: 'feature',
        category: t('shared.data-prep.category-feature'),
        layers: featureLayers,
        includeWhenEmpty: false,
      },
    ];
  });

  const isSearchActive = (): boolean => filterString().trim().length > 0;

  const isCollapsed = (categoryKey: LayerCategoryKey): boolean => {
    if (isSearchActive()) {
      return false;
    }

    return collapsedCategoryKeys().has(categoryKey);
  };

  const setCollapsed = (
    categoryKey: LayerCategoryKey,
    collapsed: boolean
  ): void => {
    setCollapsedCategoryKeys((prev) => {
      const next = new Set(prev);
      if (collapsed) {
        next.add(categoryKey);
      } else {
        next.delete(categoryKey);
      }
      return next;
    });
  };

  const visibleCategories = createMemo(() =>
    layersByCategory().filter(
      (category) => category.includeWhenEmpty || category.layers.length > 0
    )
  );

  createEffect(
    on([selectedGuids], () => {
      const selected = filteredItems().filter((item) =>
        selectedGuids().has(item.guid)
      );
      props.handleSelectedItems?.(selected);
    })
  );

  onMount(() => {
    if (!customElements.get('trb-dropdown-select')) {
      defineDropdownSelect();
    }

    if (
      items.filter((item) => isIsolatable(item) && item.isIsolated).length > 0
    ) {
      setLayersScopeDropdown(LayerScopeDropdownOptions.Isolated);
    } else {
      setLayersScopeDropdown(LayerScopeDropdownOptions.All);
    }
  });

  // Cleanup: Remove temporary anchor elements if component unmounts while popup is open
  onCleanup(() => {
    const editAnchor = editPopupAnchor();
    if (editAnchor && editAnchor.parentNode) {
      editAnchor.parentNode.removeChild(editAnchor);
    }
    closeRename();
    const viewAnchor = viewPopupAnchor();
    if (viewAnchor && viewAnchor.parentNode) {
      viewAnchor.parentNode.removeChild(viewAnchor);
    }
    layerMenuTriggers.clear();
  });

  // Helper function to handle normal click selection (toggle if only one selected, otherwise select)
  const handleNormalClickSelection = (item: LayerItemObject): void => {
    const guidsSelected = selectedGuids();
    if (guidsSelected.has(item.guid) && guidsSelected.size === 1) {
      setSelectedGuids(new Set<string>());
    } else {
      setSelectedGuids(new Set([item.guid]));
    }
  };

  // Helper function to deselect an item
  const deselectItem = (item: LayerItemObject): void => {
    const guidsSelected = selectedGuids();
    if (guidsSelected.has(item.guid)) {
      guidsSelected.delete(item.guid);
      setSelectedGuids(new Set<string>(guidsSelected));
    }
  };

  const clearSelection = (): void => {
    setSelectedGuids(new Set<string>([]));
  };

  const handleSelection = (e: PointerEvent, item: LayerItemObject): void => {
    e.stopImmediatePropagation();
    e.stopPropagation();

    const guidsSelected = selectedGuids();
    // if it is a ctrl click, add the item's guid to selected guids
    // if it is a shift click, add all items between the last selected item and the current item to selected guids
    // if it is a normal click, clear the selected guids and add the current item to selected guids
    if (e.ctrlKey || e.metaKey) {
      if (guidsSelected.has(item.guid)) {
        deselectItem(item);
      } else {
        setSelectedGuids(new Set(guidsSelected.add(item.guid)));
      }
    } else if (e.shiftKey) {
      const items = layersByCategory().flatMap((x) => x.layers);
      const currentIndex = items.findIndex((i) => i.guid === item.guid);

      // Find the last selected item in the list
      let lastSelectedIndex = -1;
      for (let i = items.length - 1; i >= 0; i--) {
        if (guidsSelected.has(items[i].guid)) {
          lastSelectedIndex = i;
          break;
        }
      }

      // If no previous selection found, use current index
      if (lastSelectedIndex === -1) {
        lastSelectedIndex = currentIndex;
      }

      // Select all items between lastSelectedIndex and currentIndex
      const newSelectedGuids = new Set<string>();
      const startIndex = Math.min(lastSelectedIndex, currentIndex);
      const endIndex = Math.max(lastSelectedIndex, currentIndex);

      for (let i = startIndex; i <= endIndex; i++) {
        newSelectedGuids.add(items[i].guid);
      }

      setSelectedGuids(newSelectedGuids);
    } else {
      handleNormalClickSelection(item);
      props.handleItemActivate?.(item);
    }
  };

  const isolateLayer = (item: LayerItemObject): void => {
    const itemsToIsolate =
      selectedGuids().size > 1 && selectedGuids().has(item.guid)
        ? items.filter((x) => selectedGuids().has(x.guid))
        : [item];
    props.handleLayerIsolationChange?.(itemsToIsolate, true);
    const invisibleItems = itemsToIsolate.filter((x) => !x.visible);
    if (invisibleItems.length > 0) {
      props.handleToggledItemVisibility(invisibleItems, true);
    }
    changeLayersScope(LayerScopeDropdownOptions.Isolated);
  };

  const unisolateLayer = (item: LayerItemObject): void => {
    const itemsToUnisolate =
      selectedGuids().size > 1 && selectedGuids().has(item.guid)
        ? items.filter((x) => selectedGuids().has(x.guid))
        : [item];
    props.handleLayerIsolationChange?.(itemsToUnisolate, false);
    changeLayersScope(LayerScopeDropdownOptions.All);
  };

  const changeLayersScope = (key: LayerScopeDropdownOptions | null): void => {
    if (key == null) {
      return;
    }
    if (key !== layersScopeDropdown()) {
      props.handleLayerScopeChange?.(key);
      setLayersScopeDropdown(key);
    }
  };

  // Helper function to create a temporary anchor element with common styling
  const createAnchorElement = (left: string, top: string): HTMLElement => {
    const tempAnchor = document.createElement('div');
    tempAnchor.style.position = 'fixed';
    tempAnchor.style.left = left;
    tempAnchor.style.top = top;
    tempAnchor.style.width = '1px';
    tempAnchor.style.height = '1px';
    tempAnchor.style.pointerEvents = 'none';
    document.body.appendChild(tempAnchor);
    return tempAnchor;
  };

  // Helper function to create a temporary anchor element for popups
  const createPopupAnchor = (event: Event): HTMLElement => {
    if (event instanceof MouseEvent) {
      return createAnchorElement(`${event.clientX}px`, `${event.clientY}px`);
    }

    // Fallback for keyboard events: use the target element or a default position
    const targetElement =
      (event.target as HTMLElement) ||
      document.querySelector('[data-testid="list-item"]');
    if (targetElement) {
      const rect = targetElement.getBoundingClientRect();
      // Position anchor at the center of the target element horizontally, below it vertically
      const centerX = rect.left + rect.width / 2; // eslint-disable-line no-magic-numbers
      return createAnchorElement(`${centerX}px`, `${rect.bottom}px`);
    }

    // Last resort: center of viewport
    return createAnchorElement('50%', '50%');
  };

  const setLayerMenuTrigger = (
    layerGuid: string,
    element: HTMLElement | undefined
  ): void => {
    if (element) {
      layerMenuTriggers.set(layerGuid, element);
    } else {
      layerMenuTriggers.delete(layerGuid);
    }
  };

  const createPopupAnchorForLayerGuid = (
    layerGuid: string,
    fallbackEvent: Event
  ): HTMLElement => {
    const trigger = layerMenuTriggers.get(layerGuid);
    if (trigger) {
      const rect = trigger.getBoundingClientRect();
      return createAnchorElement(`${rect.right}px`, `${rect.bottom}px`);
    }
    return createPopupAnchor(fallbackEvent);
  };

  const handleToggleItemVisibility = (item: LayerItemObject): void => {
    if (selectedGuids().size > 1 && selectedGuids().has(item.guid)) {
      props.handleToggledItemVisibility(
        items.filter((x) => selectedGuids().has(x.guid)),
        !item.visible
      );
    } else {
      props.handleToggledItemVisibility([item], !item.visible);
    }
  };

  const handleContextMenu = (event: IconContextMenuSelectEvent): void => {
    const item = filteredItems().find((i) => i.guid === event.value);
    if (!item) return;

    // Only layers can have Edit/View context menu items
    if (!item.isSurface && 'lineStyle' in item.object) {
      const layer = item.object as Layer;
      const menuItem = event.menuItem as ContextMenuItem;
      switch (menuItem) {
        case ContextMenuItem.Edit: {
          layerListPanelTracker.track(LayerListPanelAnalyticsEvent.LayerEdit);
          const tempAnchor = createPopupAnchorForLayerGuid(
            item.guid,
            event.event
          );
          setEditPopupAnchor(tempAnchor);
          setEditingLayer(layer);
          break;
        }
        case ContextMenuItem.View: {
          layerListPanelTracker.track(LayerListPanelAnalyticsEvent.LayerView);
          const tempAnchor = createPopupAnchorForLayerGuid(
            item.guid,
            event.event
          );
          setViewPopupAnchor(tempAnchor);
          setViewingLayer(layer);
          break;
        }
        case ContextMenuItem.SelectItem: {
          layerListPanelTracker.track(
            LayerListPanelAnalyticsEvent.LayerSelectFromContextMenu
          );

          props.handleActiveLayerChange?.(
            items.filter((x) => x.guid === item.guid)
          );
          break;
        }
        case ContextMenuItem.SelectAll: {
          layerListPanelTracker.track(
            LayerListPanelAnalyticsEvent.LayerSelectFromContextMenu
          );

          props.handleActiveLayerChange?.(
            items.filter((x) => selectedGuids().has(x.guid))
          );
          break;
        }
        case ContextMenuItem.Delete: {
          layerListPanelTracker.track(LayerListPanelAnalyticsEvent.LayerDelete);
          props.handleLayerDelete?.([item.guid]);
          break;
        }
        case ContextMenuItem.IsolateLayer: {
          layerListPanelTracker.track(
            LayerListPanelAnalyticsEvent.LayerIsolateFromContextMenu
          );
          isolateLayer(item);
          break;
        }
        case ContextMenuItem.UnisolateLayer: {
          layerListPanelTracker.track(
            LayerListPanelAnalyticsEvent.LayerUnisolateFromContextMenu
          );
          unisolateLayer(item);
          break;
        }
        case ContextMenuItem.FitToView: {
          props.handleFitToView?.(
            item.editable,
            parseFloat(item.id),
            undefined
          );
          break;
        }
        case ContextMenuItem.Rename:
        case ContextMenuItem.ExportToProjectData:
        case ContextMenuItem.Download:
          break;
        default:
          throw new UnreachableCaseError(menuItem);
      }
    } else {
      const menuItem = event.menuItem as ContextMenuItem;
      switch (menuItem) {
        case ContextMenuItem.FitToView: {
          props.handleFitToView?.(item.editable, undefined, item.guid);
          break;
        }
        case ContextMenuItem.Edit: {
          props.handleSurfaceEdit?.(item);
          break;
        }
        case ContextMenuItem.Rename: {
          if (item.category !== 'pdfPage') {
            break;
          }
          const tempAnchor = createPopupAnchorForLayerGuid(
            item.guid,
            event.event
          );
          openRename(item, tempAnchor);
          break;
        }
        case ContextMenuItem.SelectItem: {
          layerListPanelTracker.track(
            LayerListPanelAnalyticsEvent.LayerSelectFromContextMenu
          );

          props.handleActiveLayerChange?.(
            items.filter((x) => item.guid === x.guid)
          );
          break;
        }
        case ContextMenuItem.SelectAll: {
          layerListPanelTracker.track(
            LayerListPanelAnalyticsEvent.LayerSelectFromContextMenu
          );

          props.handleActiveLayerChange?.(
            items.filter((x) => selectedGuids().has(x.guid))
          );
          break;
        }
        case ContextMenuItem.Delete: {
          props.handleDeleteItem?.(item);
          break;
        }
        case ContextMenuItem.ExportToProjectData: {
          props.handleSurfaceExportToProjectData?.(item);
          break;
        }
        case ContextMenuItem.Download: {
          props.handleSurfaceDownload?.(item);
          break;
        }
        case ContextMenuItem.View:
        case ContextMenuItem.IsolateLayer:
        case ContextMenuItem.UnisolateLayer:
          break;
        default:
          throw new UnreachableCaseError(menuItem);
      }
    }
  };

  const getContextMenuItems = (
    item: LayerItemObject
    // eslint-disable-next-line sonarjs/cognitive-complexity -- MERCATOR-713: complexity 33 > 15 allowed; refactor later
  ): { label: string; value: string }[] => {
    const items: { label: string; value: string }[] = [];
    if (item.showContextMenu === false) {
      return items;
    }

    // PDF pages: Select + Rename/Delete when editing the page's drafting space.
    if (item.category === 'pdfPage') {
      items.push({
        label: t('shared.data-prep.select-layer'),
        value: ContextMenuItem.SelectItem,
      });
      if (!props.isViewOnly && props.handlePdfPageNameChange) {
        items.push({
          label: t('general.rename'),
          value: ContextMenuItem.Rename,
        });
      }
      if (!props.isViewOnly && props.handleDeleteItem) {
        items.push({
          label: t('general.delete'),
          value: ContextMenuItem.Delete,
        });
      }
      return items;
    }

    const isIsolated = item.isIsolated;

    if (props.handleFitToView) {
      items.push({
        label: t('shared.design-package-manager.fit-to-view'),
        value: ContextMenuItem.FitToView,
      });
    }

    if (!item.isSurface && 'lineStyle' in item.object) {
      if (item.editable && !props.isViewOnly) {
        items.push({
          label: t('general.edit'),
          value: ContextMenuItem.Edit,
        });
      } else if (!props.hideLayerViewAction) {
        items.push({
          label: t('general.view'),
          value: ContextMenuItem.View,
        });
      }
    } else if (
      item.isSurface &&
      item.editable &&
      item.supportsSurfaceEditor &&
      !props.isViewOnly &&
      props.handleSurfaceEdit
    ) {
      items.push({
        label: t('general.edit'),
        value: ContextMenuItem.Edit,
      });
    }

    if (props.handleActiveLayerChange) {
      items.push({
        label: item.isSurface
          ? t('shared.data-prep.select-surface')
          : t('shared.data-prep.select-layer'),
        value: ContextMenuItem.SelectItem,
      });

      if (selectedGuids().size > 1 && selectedGuids().has(item.guid)) {
        items.push({
          label: t('shared.data-prep.select-all'),
          value: ContextMenuItem.SelectAll,
        });
      }
    }

    if (
      !item.isSurface &&
      !item.isDefault &&
      item.editable &&
      !props.isViewOnly &&
      props.handleLayerDelete
    ) {
      items.push({
        label: t('general.delete'),
        value: ContextMenuItem.Delete,
      });
    }

    if (
      item.isSurface &&
      item.editable &&
      !props.isViewOnly &&
      props.handleDeleteItem
    ) {
      items.push({
        label: t('general.delete'),
        value: ContextMenuItem.Delete,
      });
    }

    if (
      item.isSurface &&
      item.editable &&
      props.handleSurfaceExportToProjectData
    ) {
      items.push({
        label: t('shared.data-prep.export-to-project-data'),
        value: ContextMenuItem.ExportToProjectData,
      });
    }

    if (item.isSurface && item.editable && props.handleSurfaceDownload) {
      items.push({
        label: t('general.download'),
        value: ContextMenuItem.Download,
      });
    }

    if (!item.isSurface && isIsolatable(item)) {
      if (!isIsolated) {
        items.push({
          label: t('shared.data-prep.isolate-layer'),
          value: ContextMenuItem.IsolateLayer,
        });
      } else {
        items.push({
          label: t('shared.data-prep.unisolate-layer'),
          value: ContextMenuItem.UnisolateLayer,
        });
      }
    }

    return items;
  };

  const [isSearchOpen, setIsSearchOpen] = createSignal<boolean>(false);

  return (
    <>
      <div
        data-testid="layers-list-panel"
        class="contents"
        onClick={clearSelection}
      >
        <PanelLayoutWithHeaderToggle
          panelTitle={t('shared.data-prep.layers-title')}
          subHeaderContent={
            <trb-dropdown-select
              prop:options={isolatedLayersDropdownOptions}
              prop:value={layersScopeDropdown()}
              prop:onValueChange={(value) => {
                if (
                  value === LayerScopeDropdownOptions.All ||
                  value === LayerScopeDropdownOptions.Isolated
                ) {
                  changeLayersScope(value);
                }
              }}
              prop:position="left"
              prop:showOnScroll={true}
            />
          }
          rightActions={
            <>
              <Show when={props.designId && !props.isViewOnly}>
                <IconButton
                  color={BtnClasses.TERTIARY}
                  icon="add"
                  size="icon-medium"
                  clicked={() => setIsCreatePopupVisible(true)}
                />
              </Show>
              <Show when={props.handleToggleAllLayersVisibility}>
                <IconButton
                  color={BtnClasses.TERTIARY}
                  icon={
                    allLayersVisible()
                      ? 'tc-icon-eye-visibility'
                      : 'tc-icon-eye-visibility-off'
                  }
                  size="icon-medium"
                  title={t(
                    allLayersVisible()
                      ? 'shared.data-prep.hide-all-layers'
                      : 'shared.data-prep.show-all-layers'
                  )}
                  clicked={() =>
                    props.handleToggleAllLayersVisibility?.(!allLayersVisible())
                  }
                />
              </Show>
              <IconButton
                color={BtnClasses.TERTIARY}
                icon="tc-icon-search"
                size="icon-medium"
                clicked={() => setIsSearchOpen(true)}
              />
            </>
          }
          toggleContent={
            <PanelHeaderSearchInput
              filterString={filterString}
              setFilterString={setFilterString}
              setIsSearchOpen={setIsSearchOpen}
              placeHolderText={t('general.searchLayers')}
              onClick={() => {
                layerListPanelTracker.track(
                  LayerListPanelAnalyticsEvent.LayerFilter
                );
              }}
            />
          }
          showToggleContent={isSearchOpen()}
          content={
            <>
              <Show
                when={items.length}
                fallback={
                  <div class="flex flex-col items-center justify-center h-full text-center text-gray-6">
                    <div class="w-10">
                      <modus-wc-icon name="layer" size="lg" />
                    </div>
                    <h3
                      class="mb-2 font-semibold"
                      textContent={t('shared.data-prep.no-layers-title')}
                    />
                    <p
                      class="w-60"
                      textContent={t('shared.data-prep.no-layers-message')}
                    />
                  </div>
                }
              >
                <For each={visibleCategories()}>
                  {(category) => (
                    <div
                      data-testid="layer-category-section"
                      data-category-key={category.categoryKey}
                    >
                      <div
                        data-testid="layer-category-header"
                        class="pl-2 pr-[10px] h-[46px] flex items-center gap-2 justify-between"
                        onClick={() => {
                          setCollapsed(
                            category.categoryKey,
                            !isCollapsed(category.categoryKey)
                          );
                        }}
                      >
                        <div textContent={category.category} />
                        <modus-wc-icon
                          name={
                            isCollapsed(category.categoryKey)
                              ? 'expand_less'
                              : 'expand_more'
                          }
                          size="sm"
                          class="cursor-pointer"
                        />
                      </div>
                      <Show
                        when={
                          !isCollapsed(category.categoryKey) &&
                          category.layers.length > 0
                        }
                      >
                        {/* eslint-disable-next-line custom/no-modus-v1 -- TWENG-132 */}
                        <modus-list class={'overflow-auto'}>
                          <For each={category.layers}>
                            {(item) => (
                              <LayerItem
                                item={item}
                                isSelected={selectedGuids().has(item.guid)}
                                isActive={props.activeItemId === item.id}
                                isLoading={
                                  props.loadingIds?.includes(item.id) ?? false
                                }
                                menuItems={getContextMenuItems(item)}
                                onSelection={handleSelection}
                                onToggleVisibility={handleToggleItemVisibility}
                                onContextMenuSelection={handleContextMenu}
                                onMenuTriggerRef={setLayerMenuTrigger}
                              />
                            )}
                          </For>
                        </modus-list>
                      </Show>
                    </div>
                  )}
                </For>
              </Show>
              <Show when={editingLayer() && editPopupAnchor()}>
                <LayerEditPopup
                  layer={editingLayer()!}
                  anchorElement={editPopupAnchor()!}
                  onClose={() => {
                    // Clean up temporary anchor if it exists
                    const anchor = editPopupAnchor();
                    if (anchor && anchor.parentNode) {
                      anchor.parentNode.removeChild(anchor);
                    }
                    setEditingLayer(undefined);
                    setEditPopupAnchor(undefined);
                  }}
                  onNameChange={(layerIds, newName) =>
                    props.handleLayerNameChange?.(layerIds, newName)
                  }
                  onColorChange={(layerIds, color) =>
                    props.handleLayerColorChange?.(layerIds, color)
                  }
                  onLineStyleChange={(layerIds, lineStyle) =>
                    props.handleLayerLineStyleChange?.(layerIds, lineStyle)
                  }
                  onLineThicknessChange={(layerIds, lineThickness) =>
                    props.handleLayerLineThicknessChange?.(
                      layerIds,
                      lineThickness
                    )
                  }
                />
              </Show>
              <Show when={renamePopup.item && renamePopup.anchor}>
                <LayerRenamePopup
                  name={renamePopup.item!.name}
                  anchorElement={renamePopup.anchor!}
                  onClose={closeRename}
                  onNameChange={(newName) => {
                    const allItems = items
                      .filter(
                        (x) =>
                          x.category === 'pdfPage' &&
                          x.guid !== renamePopup.item?.guid
                      )
                      .map((x) => x.name.toLowerCase().trim());
                    const item = renamePopup.item;
                    const pageName = newName.trim();
                    if (
                      !item ||
                      pageName.length === 0 ||
                      pageName === renamePopup.item?.name ||
                      allItems.indexOf(pageName.toLowerCase()) >= 0
                    ) {
                      return;
                    }
                    props.handlePdfPageNameChange?.(item.guid, pageName);
                  }}
                />
              </Show>
              <Show when={viewingLayer() && viewPopupAnchor()}>
                <LayerViewPopup
                  layer={viewingLayer()!}
                  anchorElement={viewPopupAnchor()!}
                  onClose={() => {
                    const anchor = viewPopupAnchor();
                    if (anchor && anchor.parentNode) {
                      anchor.parentNode.removeChild(anchor);
                    }
                    setViewingLayer(undefined);
                    setViewPopupAnchor(undefined);
                  }}
                />
              </Show>
              <Show when={isCreatePopupVisible()}>
                <div class="fixed top-0 left-0 z-[1500] w-full h-full flex justify-center items-center">
                  <div class="w-full h-full bg-black opacity-25 absolute z-[1501]" />
                  <div class="w-[500px] bg-white z-[1502] p-5">
                    <LayerCreatePopup
                      onLayerCreate={(e) => {
                        props.handleLayerCreate?.(e);
                      }}
                      onClose={() => {
                        setIsCreatePopupVisible(false);
                      }}
                    />
                  </div>
                </div>
              </Show>
            </>
          }
        />
      </div>
    </>
  );
};
